import { useContext, useMemo, useSyncExternalStore } from "react";

import { resolveLanding } from "./access";
import { usePlatform } from "./platform";
import type { SessionController } from "./controller";
import { SessionContext, SessionControllerContext } from "./SessionProvider";
import type { Landing, SessionState, SessionStore } from "./types";

export function useSession(): SessionState {
  const store = useContext(SessionContext);
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );
}

export function useSessionActions(): Pick<
  SessionStore,
  "signIn" | "setActiveRole" | "signOut"
> {
  const store = useContext(SessionContext);
  return useMemo(
    () => ({
      signIn: store.signIn,
      setActiveRole: store.setActiveRole,
      signOut: store.signOut,
    }),
    [store],
  );
}

export function useLanding(): Landing {
  return resolveLanding(useSession(), usePlatform());
}

/** 002: every sign-in method hands its result here (FR-002; P-AUTH-4). */
export function useCompleteSignIn(): SessionController["completeSignIn"] {
  return useContext(SessionControllerContext).completeSignIn;
}

/** 002: Log out, renewal (Refresh access) and the post-sign-in destination. */
export function useSessionController(): Pick<
  SessionController,
  | "signOut"
  | "renew"
  | "takePostSignInHref"
  | "peekPostSignInHref"
  | "switchRole"
  | "refreshAccess"
> {
  const controller = useContext(SessionControllerContext);
  return useMemo(
    () => ({
      signOut: controller.signOut,
      renew: controller.renew,
      takePostSignInHref: controller.takePostSignInHref,
      peekPostSignInHref: controller.peekPostSignInHref,
      switchRole: controller.switchRole,
      refreshAccess: controller.refreshAccess,
    }),
    [controller],
  );
}

/** 002: Try again on a restore that failed for a reason worth retrying (FR-023). */
export function useRetryRestore(): () => Promise<void> {
  return useContext(SessionContext).retryRestore;
}
